import { History, Trash2, Wallet } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { confirmModal } from '@/components/ui/modal'
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table'
import { fmtMoney } from '@/features/shared/format'
import { Select } from '@/components/ui/select'
import { SgkTesvikCard } from '../components/bordro/SgkTesvikCard'
import { KpiCard } from '../components/KpiCard'
import { MobileList } from '../components/MobileList'
import { SectionCard } from '../components/SectionCard'
import { BordroStatusPill } from '../components/StatusPill'
import { TABLE_FRAME } from '../components/tableFrame'
import type { PersonelDetail, PersonelProfile } from '../model'
import { downloadPayslip } from '../lib/bordroActions'
import { printDoc } from '../lib/exportActions'
import type { Personel } from '../types'
import { useDeleteHistoricalPayroll } from '../api'
import { GecmisBordroModal } from '../components/actions/GecmisBordroModal'
import { HR_PAYROLL_WRITE_PERM } from '../../permissions'
import { useAuth } from '@/lib/auth/AuthProvider'
import { apiErrorMessage } from '@/i18n'

export function UcretBordroTab({ personel, ucret, sigorta }: { personel: Personel; ucret: PersonelDetail['ucret']; sigorta?: PersonelProfile['sigorta'] }) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const canWrite = can(HR_PAYROLL_WRITE_PERM)
  const [gecmisOpen, setGecmisOpen] = useState(false)
  const removeHistorical = useDeleteHistoricalPayroll()
  // Seçili dönem API biçiminde (YYYY-MM) tutulur; etiket yalnız gösterim içindir.
  const [selected, setSelected] = useState('')
  const period = ucret.gecmis.some((g) => g.period === selected) ? selected : (ucret.gecmis[0]?.period ?? '')
  const pdf = async () => {
    if (await downloadPayslip(personel.id, period)) return
    const row = ucret.gecmis.find((g) => g.period === period)
    printDoc({
      title: `${t('hr.personel.actions.bordro.pdf')} · ${row?.donem ?? period}`,
      subtitle: `${personel.ad} · ${personel.sicil} · ${personel.pozisyon}`,
      sections: [
        { heading: t('hr.personel.ucret.kazanclar'), rows: ucret.kazanc.map((k) => [k.label, fmtMoney(k.amount)]), total: [t('hr.personel.ucret.brut'), fmtMoney(row?.brut ?? ucret.brut)] },
        { heading: t('hr.personel.ucret.kesintiler'), rows: ucret.kesinti.map((k) => [k.label, fmtMoney(k.amount)]), total: [t('hr.personel.ucret.kesintiler'), fmtMoney(ucret.kesintiToplam)] },
        { rows: [[t('hr.personel.columns.durum'), row ? t(`hr.personel.bordro.durum.${row.durum}`) : '—']], total: [t('hr.personel.ucret.net'), fmtMoney(row?.net ?? ucret.net)] },
      ],
    })
  }
  const remove = async (row: PersonelDetail['ucret']['gecmis'][number]) => {
    const ok = await confirmModal({
      title: t('hr.personel.gecmisBordro.deleteTitle'),
      description: t('hr.personel.gecmisBordro.deleteBody', { period: row.donem }),
      confirmLabel: t('common.delete'),
      destructive: true,
    })
    if (!ok) return
    removeHistorical.mutate(
      { employeeId: personel.id, payrollId: row.id },
      { onSuccess: () => toast.success(t('hr.personel.gecmisBordro.deleted')), onError: (e) => toast.error(apiErrorMessage(e)) },
    )
  }
  const sourceBadge = (row: PersonelDetail['ucret']['gecmis'][number]) =>
    row.gecmisAktarim ? (
      <Badge tone="info" className="ml-2">
        {t('hr.personel.gecmisBordro.badge')}
      </Badge>
    ) : null
  return (
    <div className="space-y-4">
      <SgkTesvikCard personelId={personel.id} sigorta={sigorta} canWrite={canWrite} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Select
          size="lg"
          className="w-auto min-w-[9.5rem] rounded-xl"
          value={period}
          onChange={setSelected}
          aria-label={t('hr.personel.ucret.donem')}
          options={ucret.gecmis.map((g) => ({ value: g.period, label: g.donem }))}
        />
        <div className="flex flex-wrap gap-2">
          {canWrite ? (
            <Button variant="outline" size="sm" onClick={() => setGecmisOpen(true)}>
              <History />
              {t('hr.personel.gecmisBordro.open')}
            </Button>
          ) : null}
          <Button variant="outline" size="sm" disabled={!period} onClick={() => void pdf()}>
            {t('hr.personel.ucret.pdf')}
          </Button>
        </div>
      </div>
      {gecmisOpen ? <GecmisBordroModal personelId={personel.id} onClose={() => setGecmisOpen(false)} /> : null}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard label={t('hr.personel.ucret.brut')} value={fmtMoney(ucret.brut)} tint="info" icon={<Wallet className="size-5" />} />
        <KpiCard label={t('hr.personel.ucret.net')} value={fmtMoney(ucret.net)} tint="success" icon={<Wallet className="size-5" />} />
        <KpiCard label={t('hr.personel.ucret.yan')} value={fmtMoney(ucret.yan)} tint="primary" icon={<Wallet className="size-5" />} />
        <KpiCard label={t('hr.personel.ucret.iseveren')} value={fmtMoney(ucret.iseveren)} tint="warning" icon={<Wallet className="size-5" />} />
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)] gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_280px]">
        <SectionCard title={t('hr.personel.ucret.kazanclar')}>
          <div className="space-y-2">
            {ucret.kazanc.map((k) => (
              <div key={k.label} className="flex items-center justify-between gap-2 border-b border-border/60 py-2 last:border-0 text-sm">
                <span className="text-muted-foreground">{k.label}</span>
                <span className="tabular-nums font-medium">{fmtMoney(k.amount)}</span>
              </div>
            ))}
          </div>
        </SectionCard>
        <SectionCard title={t('hr.personel.ucret.kesintiler')}>
          <div className="space-y-2">
            {ucret.kesinti.map((k) => (
              <div key={k.label} className="flex items-center justify-between gap-2 border-b border-border/60 py-2 last:border-0 text-sm">
                <span className="text-muted-foreground">{k.label}</span>
                <span className="tabular-nums font-medium text-destructive">{fmtMoney(k.amount)}</span>
              </div>
            ))}
          </div>
        </SectionCard>
        <SectionCard title={t('hr.personel.ucret.donemOzet')}>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">{t('hr.personel.ucret.brut')}</span>
              <span className="tabular-nums font-medium">{fmtMoney(ucret.brut)}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">{t('hr.personel.ucret.yan')}</span>
              <span className="tabular-nums font-medium">{fmtMoney(ucret.yan)}</span>
            </div>
            <div className="flex justify-between gap-2">
              <span className="text-muted-foreground">{t('hr.personel.ucret.kesintiler')}</span>
              <span className="tabular-nums font-medium text-destructive">{fmtMoney(ucret.kesintiToplam)}</span>
            </div>
            <div className="rounded-lg bg-success/10 px-3 py-2.5 font-semibold text-success">
              <div className="flex justify-between gap-2">
                <span>{t('hr.personel.ucret.net')}</span>
                <span className="tabular-nums">{fmtMoney(ucret.net)}</span>
              </div>
            </div>
          </div>
        </SectionCard>
      </div>

      <SectionCard title={t('hr.personel.ucret.gecmis')} noPad>
        <MobileList
          rows={ucret.gecmis.map((r) => ({
            key: r.id,
            title: (
              <>
                {r.donem}
                {sourceBadge(r)}
              </>
            ),
            trailing: <BordroStatusPill durum={r.durum} />,
            meta: (
              <>
                <span>
                  {t('hr.personel.ucret.brut')}: <span className="tabular-nums text-foreground">{fmtMoney(r.brut)}</span>
                </span>
                <span aria-hidden>·</span>
                <span>
                  {t('hr.personel.ucret.net')}: <span className="font-medium tabular-nums text-foreground">{fmtMoney(r.net)}</span>
                </span>
              </>
            ),
          }))}
        />
        <Table className="hr-table" wrapperClassName={TABLE_FRAME}>
          <THead>
            <TR>
              <TH>{t('hr.personel.ucret.donem')}</TH>
              <TH className="text-right">{t('hr.personel.ucret.brut')}</TH>
              <TH className="text-right">{t('hr.personel.ucret.net')}</TH>
              <TH>{t('hr.personel.columns.durum')}</TH>
              {canWrite ? <TH className="w-10" /> : null}
            </TR>
          </THead>
          <TBody>
            {ucret.gecmis.map((r) => (
              <TR key={r.id}>
                <TD className="font-medium">
                  {r.donem}
                  {sourceBadge(r)}
                </TD>
                <TD className="text-right tabular-nums">{fmtMoney(r.brut)}</TD>
                <TD className="text-right tabular-nums font-medium">{fmtMoney(r.net)}</TD>
                <TD>
                  <BordroStatusPill durum={r.durum} />
                </TD>
                {canWrite ? (
                  <TD>
                    {r.gecmisAktarim ? (
                      <Button variant="ghost" size="icon" aria-label={t('common.delete')} disabled={removeHistorical.isPending} onClick={() => void remove(r)}>
                        <Trash2 />
                      </Button>
                    ) : null}
                  </TD>
                ) : null}
              </TR>
            ))}
          </TBody>
        </Table>
      </SectionCard>
    </div>
  )
}
